import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useGateValue } from '@statsig/react-bindings';
import { Space, Container } from '@ag1/ui';

import {
  type VideoReviewsTabbed as VideoReviewsTabbedProps,
  type SanityKeyed
} from 'types/studio/studio';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { SSGates } from '@portal/state/context/StatsigContext/StatsigFlags';
import { MediaProvider } from '@portal/state/context/MediaContext';

import { useGetV53WrittenReviews } from '@portal/state/atlas/getV53WrittenReviews';
import { useHandleTabChange } from './hooks/useHandleTabChange';
import styles from './VideoReviewsTabbed.module.scss';
import { CommonSectionProps } from '../types';
import { V53WrittenReviews } from './V53WrittenReviews/V53WrittenReviews';
import { VideoReviews } from './VideoReviews/VideoReviews';
import { WrittenReviews } from './WrittenReviews/WrittenReviews';
import { SCROLL_TO_TOP_OFFSET } from './constants';
import { WRITTEN_REVIEW_PAGINATION_LIMIT } from './V53WrittenReviews/constants';

export const VideoReviewsTabbed = ({
  _key: key,
  videoReviewsTabText,
  writtenReviewsTabText,
  padding,
  ...props
}: SanityKeyed<VideoReviewsTabbedProps> & CommonSectionProps) => {
  const enableNewReviewsUI = useGateValue(SSGates.enableNewReviewsUI);

  // Logic for tab change between video and written videos + active border animation
  const { activeTab, activeTabBorder, setActiveTab, tabLinksContainer } = useHandleTabChange();

  const isVideoReviewsTabActive = useMemo(() => activeTab === 0, [activeTab]);
  const isWrittenReviewsTabActive = useMemo(() => activeTab === 1, [activeTab]);

  const v53WrittenReviewsApiResponse = useGetV53WrittenReviews({
    sortBy: 'date',
    sortOrder: 'desc',
    pageSize: WRITTEN_REVIEW_PAGINATION_LIMIT
  });

  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      if (tabLinksContainer.current) {
        const { top, height } = tabLinksContainer.current.getBoundingClientRect();
        window.scrollTo({
          top: top - height + window.scrollY + SCROLL_TO_TOP_OFFSET,
          behavior: 'smooth'
        });
      }
    }, 100);
  }, [tabLinksContainer]);

  return (
    <MediaProvider>
      <Container as="section" narrow id={key}>
        <Space className={clsx(getSectionPaddingClasses(styles, padding))}>
          <div className={styles.tabsContainer} ref={tabLinksContainer}>
            <button
              type="button"
              className={clsx(styles.tabButton, isVideoReviewsTabActive && styles.activeTabButon)}
              onClick={() => setActiveTab(0)}>
              {videoReviewsTabText}
            </button>
            <button
              type="button"
              className={clsx(styles.tabButton, isWrittenReviewsTabActive && styles.activeTabButon)}
              onClick={() => setActiveTab(1)}>
              {writtenReviewsTabText}
            </button>
            <div className={styles.activeTabBorder} ref={activeTabBorder} />
            <div className={styles.tabsBottomBorder} />
          </div>

          {isVideoReviewsTabActive && (
            <VideoReviews _key={key} scrollToTop={scrollToTop} {...props} />
          )}

          {isWrittenReviewsTabActive &&
            (enableNewReviewsUI ? (
              <V53WrittenReviews
                apiResponse={v53WrittenReviewsApiResponse}
                scrollToTop={scrollToTop}
              />
            ) : (
              <WrittenReviews />
            ))}
        </Space>
      </Container>
    </MediaProvider>
  );
};

export default VideoReviewsTabbed;
